import { afterNextRender, AfterViewInit, ChangeDetectionStrategy, Component, DestroyRef, effect, forwardRef, model, untracked, ViewEncapsulation } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';
import { FieldType, FieldTypeConfig } from '@ngx-formly/core';
import { SliderModule } from 'primeng/slider';
import { PhxInputNumberComponent } from '../phx-input-number/phx-input-number.component';

@Component({
  selector: 'app-phx-slider',
  imports: [SliderModule, FormsModule, PhxInputNumberComponent],
  templateUrl: './phx-slider.component.html',
  encapsulation: ViewEncapsulation.Emulated,
  changeDetection: ChangeDetectionStrategy.Default,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => PhxSliderComponent),
      multi: true
    }
  ]
})
export class PhxSliderComponent extends FieldType<FieldTypeConfig> implements AfterViewInit, ControlValueAccessor {
  /** (*basically untested*) whether the slider is in range mode (value is an array of two numbers) */
  rangeMode = model<boolean>(false);
  value = model<number | number[]>(0);
  min = model<number>(0);
  max = model<number>(100);
  step = model<number>(1);
  disabled = model<boolean>(false);

  sliderValue1 = model<number>(0);
  sliderValue2 = model<number>(0);

  showInput = model<boolean>(true);

  constructor(private destroyRef: DestroyRef) {
    super();

    effect(() => {
      let rangeMode = this.rangeMode();
      //the intend is to only trigger on rangeMode changes
      let value = untracked(() => this.value());
      if (rangeMode) {
        untracked(() => {
          if (!Array.isArray(value)) {
            this.sliderValue1.set(this.props?.min ?? this.min() ?? 0);
            this.sliderValue2.set(this.props?.max ?? this.max() ?? 100);
          }
          else {
            if (value[0] != this.sliderValue1())
              this.sliderValue1.set(value[0]);
            if (value[1] != this.sliderValue2())
              this.sliderValue2.set(value[1]);
          }
        });
      }
      //TODO: i think this will overwrite the value if it is an array but rangeMode is not set yet:
      // else if (!rangeMode && Array.isArray(value)) {
      //   this.value.set(value[0] ?? 0);
      // }
    });

    effect(() => {
      let sliderValue1 = this.sliderValue1();
      let sliderValue2 = this.sliderValue2();
      if (this.rangeMode()) {
        this.value.set([sliderValue1, sliderValue2]);
      }
    });

    afterNextRender({
      read: () => {
        if (this.props?.rangeMode) {
          this.rangeMode.set(true);
        }
      }
    });
  }

  ngAfterViewInit(): void {
    if (this.formControl) {
      this.value.set(this.formControl.value);
      this.formControl.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((value) => {
        if (Array.isArray(value)) {
          if (value[0] != this.sliderValue1() || value[1] != this.sliderValue2()) {
            untracked(() => {
              this.sliderValue1.set(value[0]);
              this.sliderValue2.set(value[1]);
            });
          }
        }
        if (value != this.value()) {
          this.value.set(value);
        }
      });
    }
  }

  //#region ControlValueAccessor
  onValueChange(value: number | number[]): void {
    if (this.formControl && this.formControl.value != value)
      this.formControl?.setValue(value);
    this.onChange?.(value);
  }

  onChange: (value: number | number[]) => void;
  onTouched: () => void;

  writeValue(value: number | number[]): void {
    this.formControl?.setValue(value);
    this.value.set(value);
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }
  //#endregion
}
